"use client";

import { AuthUserInfo } from "@/auth";
import { IS_LOCAL } from "@/config";
import { DD_RUM_APP_ID } from "@/lib/constants";
import { isAuthSessionFetchNetworkNoise } from "@/lib/errors/is-auth-session-fetch-network-noise";
import { isChunkLoadError } from "@/lib/errors/is-chunk-load-error";
import { isCrossOriginScriptError } from "@/lib/errors/cross-origin-script-error";
import { isFetchServerResponseAbortError } from "@/lib/errors/is-fetch-server-response-abort-error";
import { isHookCountMismatchError } from "@/lib/errors/is-hook-count-mismatch-error";
import { isIgnoredEventCancelNoise } from "@/lib/errors/is-ignored-event-cancel-noise";
import { isKnockAbortError } from "@/lib/errors/is-knock-abort-error";
import { isKnownTransientNetworkError } from "@/lib/errors/is-known-transient-network-error";
import { isMobileStackOverflowNoise } from "@/lib/errors/is-mobile-stack-overflow-noise";
import {
  hasNextRouterErrorDigest,
  isNextRouterError,
} from "@/lib/errors/is-next-router-error";
import { isSafariMediaEmptyRangesNoise } from "@/lib/errors/is-safari-media-empty-ranges-noise";
import { isResizeObserverLoopError } from "@/lib/errors/is-resize-observer-loop-error";
import { isStacklessParamsNotSetError } from "@/lib/errors/is-stackless-params-not-set-error";
import { isMicrosoftLinkScannerNoise } from "@/lib/errors/microsoft-link-scanner-noise";
import { isDatadogSdkInternalNoise } from "@/lib/errors/is-datadog-sdk-internal-noise";
import { isFeatureFlagFetchNoise } from "@/lib/errors/is-feature-flag-fetch-noise";
import { isIndexeddbCacheNoise } from "@/lib/errors/is-indexeddb-cache-noise";
import { isSafariClassAsFunctionNoise } from "@/lib/errors/is-safari-class-as-function-noise";
import { isSafariNullPropertyAccessNoise } from "@/lib/errors/is-safari-null-property-access-noise";
import { isBrowserExtensionReferenceErrorNoise } from "@/lib/errors/is-browser-extension-reference-error-noise";
import { isBrowserExtensionFetchNoise } from "@/lib/errors/is-browser-extension-fetch-noise";
import { isBrowserExtensionBackgroundPageNoise } from "@/lib/errors/is-browser-extension-background-page-noise";
import { isBrowserExtensionNoListenerNoise } from "@/lib/errors/is-browser-extension-no-listener-noise";
import { isBrowserExtensionStackNoise } from "@/lib/errors/is-browser-extension-stack-noise";
import { isInjectedScriptPropertyNoise } from "@/lib/errors/is-injected-script-property-noise";
import {
  buildLogNoFirstPartyFrameFingerprint,
  buildRumNoFirstPartyFrameFingerprint,
} from "@/lib/errors/no-first-party-frame-fingerprint";
import { isMinifiedFrameworkDigestNoise } from "@/lib/errors/is-minified-framework-digest-noise";
import { isBlobScriptStackNoise } from "@/lib/errors/is-blob-script-stack-noise";
import { isSourcelessScriptErrorNoise } from "@/lib/errors/is-sourceless-script-error-noise";
import { isThirdPartyWidgetNoise } from "@/lib/errors/is-third-party-widget-noise";
import { isThirdPartyTelemetryFetchNoise } from "@/lib/errors/third-party-telemetry-fetch-noise";
import { isTwilioMicReacquireFailure } from "@/lib/errors/is-twilio-mic-reacquire-failure";
import { isTwilioSetSinkIdNoise } from "@/lib/errors/is-twilio-set-sink-id-noise";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { OpenFeature } from "@openfeature/react-sdk";
import { useSession } from "next-auth/react";
import { useEffect } from "react";

export default function initDatadogClientside() {
  if (process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
    // Build allowedTracingUrls for first-party origins
    const allowedTracingUrls: (string | RegExp | ((url: string) => boolean))[] =
      [];
    try {
      // Get the app's origin from NEXT_PUBLIC_APP_URL or fallback to current origin
      const appUrl = process.env.NEXT_PUBLIC_APP_URL;
      const appOrigin = appUrl
        ? new URL(appUrl).origin
        : typeof window !== "undefined"
          ? window.location.origin
          : "";

      if (appOrigin) {
        // String match for the app's own domain (matches URLs starting with this origin)
        allowedTracingUrls.push(appOrigin);
      }

      // Regex to match any subdomain of marblehealth.com (for preview environments)
      // This matches patterns like preview-123.marblehealth.com, preview.marblehealth.com, etc.
      // The (\/|$) ensures the domain ends at marblehealth.com to prevent matching malicious domains
      // like preview.marblehealth.com.attacker.com
      allowedTracingUrls.push(/^https:\/\/[^\/]+\.marblehealth\.com(\/|$)/);
    } catch (error) {
      // If URL parsing fails, fallback to current origin
      if (typeof window !== "undefined") {
        allowedTracingUrls.push(window.location.origin);
        allowedTracingUrls.push((url: string) => {
          return url.startsWith(window.location.origin);
        });
      }
    }

    // Init RUM
    datadogRum.init({
      applicationId: DD_RUM_APP_ID,
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
      site: "us5.datadoghq.com",
      service: process.env.NEXT_PUBLIC_DD_SERVICE,
      env: process.env.NEXT_PUBLIC_DD_ENV,
      version: process.env.NEXT_PUBLIC_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      allowedTracingUrls:
        allowedTracingUrls.length > 0 ? allowedTracingUrls : undefined,
      beforeSend: (event, context) => {
        // In local mode, prevent events from being sent to Datadog
        if (IS_LOCAL) {
          if (process.env.NEXT_PUBLIC_DD_DEBUG) {
            // eslint-disable-next-line no-console
            console.log(
              "[Datadog RUM] Event blocked (local mode):",
              event.type,
            );
            // collect a RUM resource's response headers
            // eslint-disable-next-line no-console
            console.log("event", event);
            // eslint-disable-next-line no-console
            console.log("context", context);
          }
          return false;
        }

        // Drop failed network resource fetches (status 0) that are transient
        // browser-level failures (offline, tab backgrounded, cancelled).
        // These surface as Datadog Error Tracking issues but are not actionable.
        if (
          event.type === "resource" &&
          event.resource.status_code === 0 &&
          _isTransientResourceFetchUrl(event.resource.url)
        ) {
          return false;
        }

        // Filter out cross-origin "Script error." events (e.g. from cal.com embed iframe).
        // These errors have no stack trace and are not actionable.
        if (
          event.type === "error" &&
          isCrossOriginScriptError(event.error.message, event.error.stack)
        ) {
          return false;
        }

        // Filter known browser extension/runtime noise that does not originate
        // from application code and has no actionable stack frames.
        if (
          event.type === "error" &&
          _isKnownBrowserRuntimeNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isThirdPartyWidgetNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isSourcelessScriptErrorNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isBlobScriptStackNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isResizeObserverLoopError(event.error.message)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isIgnoredEventCancelNoise(event.error.message)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isSafariNullPropertyAccessNoise(
            event.error.message,
            event.error.stack,
          )
        ) {
          return false;
        }

        // A notFound() / redirect() thrown while rendering a Server Component
        // reaches the browser as "Minified React error #441"; only the digest
        // on the original error says it was router control flow, and these
        // still reach RUM as unhandled errors.
        if (
          event.type === "error" &&
          (isNextRouterError(event.error.message) ||
            ("error" in context && hasNextRouterErrorDigest(context.error)))
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isKnownTransientNetworkError({
            message: event.error.message,
            stack: event.error.stack,
          })
        ) {
          return false;
        }

        // next-auth's SessionProvider refetches the session on window focus,
        // so an aborted fetch also surfaces as next-auth's own console.error.
        // The matching browser log and failed resource are already dropped;
        // drop this third signal for the same abort.
        if (
          event.type === "error" &&
          isAuthSessionFetchNetworkNoise({
            message: event.error.message,
            errorMessage: undefined,
            stack: event.error.stack,
            url: undefined,
            statusCode: undefined,
          })
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isFetchServerResponseAbortError({
            message: event.error.message,
            stack: event.error.stack,
            kind: _getErrorKindOrType(event.error),
          })
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isChunkLoadError({
            name: event.error.type,
            message: event.error.message,
            kind: _getErrorKindOrType(event.error),
            stack: event.error.stack,
          })
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isKnockAbortError({
            message: event.error.message,
            stack: event.error.stack,
          })
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          _isReactHooksOrderErrorFromVendorCode(
            event.error.message,
            event.error.stack,
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          _isUnsupportedSyntaxError(
            event.error.message,
            event.error.stack,
            _getErrorKindOrType(event.error),
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isStacklessParamsNotSetError(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isMicrosoftLinkScannerNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          _isSafariFetchCancelledNoise(event.error)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isMobileStackOverflowNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isSafariMediaEmptyRangesNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isSafariClassAsFunctionNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isDatadogSdkInternalNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isFeatureFlagFetchNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isIndexeddbCacheNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isBrowserExtensionReferenceErrorNoise(
            event.error.message,
            event.error.stack,
            _getErrorKindOrType(event.error),
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isBrowserExtensionStackNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isInjectedScriptPropertyNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isBrowserExtensionNoListenerNoise(
            event.error.message,
            event.error.stack,
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isBrowserExtensionBackgroundPageNoise(
            event.error.message,
            event.error.stack,
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isBrowserExtensionFetchNoise(
            event.error.message,
            event.error.stack,
            _getErrorKindOrType(event.error),
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isTwilioSetSinkIdNoise(
            event.error.message,
            event.error.stack,
            _getErrorKindOrType(event.error),
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isTwilioMicReacquireFailure(
            event.error.message,
            event.error.stack,
            _getErrorKindOrType(event.error),
          )
        ) {
          return false;
        }

        if (
          event.type === "error" &&
          isMinifiedFrameworkDigestNoise(event.error.message, event.error.stack)
        ) {
          return false;
        }

        // Runs after every drop rule. Default grouping keys on stack frames,
        // so errors with none of ours on the stack all land in one issue under
        // a stale title. Group those by type + normalized message instead,
        // unless the error already carries a fingerprint.
        if (event.type === "error" && !event.error.fingerprint) {
          const fingerprint = buildRumNoFirstPartyFrameFingerprint({
            source: event.error.source,
            type: event.error.type,
            message: event.error.message,
            stack: event.error.stack,
          });
          if (fingerprint) {
            event.error.fingerprint = fingerprint;
          }
        }

        return true;
      },
    });

    // Init browser logs
    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
      site: "us5.datadoghq.com",
      service: process.env.NEXT_PUBLIC_DD_SERVICE,
      forwardErrorsToLogs: !IS_LOCAL, // Don't forward errors in local mode
      forwardConsoleLogs: IS_LOCAL ? undefined : "all", // Don't forward console logs in local mode
      sessionSampleRate: 100,
      env: process.env.NEXT_PUBLIC_DD_ENV,
      version: process.env.NEXT_PUBLIC_VERSION,
      beforeSend: (log) => {
        if (IS_LOCAL) {
          // eslint-disable-next-line no-console
          console.log("[Datadog Logs] Log blocked (local mode):", log);
          return false;
        }

        // Filter known non-actionable auth session fetch transport noise.
        if (
          log.status === "error" &&
          isAuthSessionFetchNetworkNoise({
            message: log.message,
            errorMessage: log.error?.message,
            stack: log.error?.stack,
            url: log.http?.url,
            statusCode: log.http?.status_code,
          })
        ) {
          return false;
        }

        // A third-party script injected by a browser add-on posting its own
        // telemetry through our patched fetch; nothing Marble called.
        if (
          log.status === "error" &&
          isThirdPartyTelemetryFetchNoise({
            origin: log.origin,
            url: log.http?.url,
            message: log.message,
          })
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isCrossOriginScriptError(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          _isKnownBrowserRuntimeNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isThirdPartyWidgetNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isSourcelessScriptErrorNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isBlobScriptStackNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (log.status === "error" && isResizeObserverLoopError(log.message)) {
          return false;
        }

        if (log.status === "error" && isIgnoredEventCancelNoise(log.message)) {
          return false;
        }

        if (
          log.status === "error" &&
          isSafariNullPropertyAccessNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (log.status === "error" && isNextRouterError(log.message)) {
          return false;
        }

        if (
          log.status === "error" &&
          isKnownTransientNetworkError({
            message: log.message,
            stack: log.error?.stack,
          })
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isFetchServerResponseAbortError({
            message: log.message,
            stack: log.error?.stack,
            kind: log.error ? _getErrorKindOrType(log.error) : undefined,
          })
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isChunkLoadError({
            name:
              typeof log.error?.type === "string" ? log.error.type : undefined,
            message: log.message,
            kind: log.error ? _getErrorKindOrType(log.error) : undefined,
            stack: log.error?.stack,
          })
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isKnockAbortError({
            message: log.message,
            stack: log.error?.stack,
          })
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          _isUnsupportedSyntaxError(
            log.message,
            log.error?.stack,
            log.error ? _getErrorKindOrType(log.error) : undefined,
          )
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isStacklessParamsNotSetError(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isMicrosoftLinkScannerNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isMobileStackOverflowNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isSafariMediaEmptyRangesNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isSafariClassAsFunctionNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isDatadogSdkInternalNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isFeatureFlagFetchNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isIndexeddbCacheNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isBrowserExtensionReferenceErrorNoise(
            log.message,
            log.error?.stack,
            log.error ? _getErrorKindOrType(log.error) : undefined,
          )
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isBrowserExtensionStackNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isInjectedScriptPropertyNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isBrowserExtensionNoListenerNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isBrowserExtensionBackgroundPageNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isBrowserExtensionFetchNoise(
            log.message,
            log.error?.stack,
            log.error ? _getErrorKindOrType(log.error) : undefined,
          )
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isTwilioSetSinkIdNoise(
            log.message,
            log.error?.stack,
            log.error ? _getErrorKindOrType(log.error) : undefined,
          )
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isTwilioMicReacquireFailure(
            log.message,
            log.error?.stack,
            log.error ? _getErrorKindOrType(log.error) : undefined,
          )
        ) {
          return false;
        }

        if (
          log.status === "error" &&
          isMinifiedFrameworkDigestNoise(log.message, log.error?.stack)
        ) {
          return false;
        }

        // Same grouping fix as the RUM chain, after every drop rule. Only
        // logs with an error kind: a fingerprint on a kind-less log (network
        // `XHR error`, plain console string) opens a new untitled issue.
        if (log.status === "error" && log.error && !log.error.fingerprint) {
          const fingerprint = buildLogNoFirstPartyFrameFingerprint({
            kind: log.error.kind,
            message: log.error.message ?? log.message,
            stack: log.error.stack,
          });
          if (fingerprint) {
            log.error.fingerprint = fingerprint;
          }
        }

        return true;
      },
    });
  }
}

export function SetObservabilityAuthUser() {
  const { data: session } = useSession();
  const authUser = session?.user as AuthUserInfo | undefined;
  const isImpersonating = session?.user?.isImpersonating ?? false;

  useEffect(() => {
    if (!authUser) {
      return;
    }

    if (process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
      datadogRum.setUser({
        id: `${authUser.id}`,
        email: authUser.email,
        name: `${authUser.firstName} ${authUser.lastName} (${authUser.userType})`,
        ...(authUser.userType === "THERAPIST" && {
          therapist_id: `${authUser.userTypeId}`,
        }),
        // Can be missing right after account setup; the next session has it.
        ...(authUser.userType === "SCHOOL_WORKER" &&
          authUser.userTypeId && {
            school_worker_id: `${authUser.userTypeId}`,
          }),
      });
      datadogRum.setGlobalContextProperty("userType", authUser.userType);
      datadogRum.setGlobalContextProperty("state", authUser.state);
      datadogRum.setGlobalContextProperty("isImpersonating", isImpersonating);

      if (authUser.schoolId && authUser.schoolName) {
        datadogRum.setAccount({
          id: `${authUser.schoolId}`,
          name: authUser.schoolName,
        });
      } else {
        datadogRum.clearAccount();
      }
    }

    void OpenFeature.setContext({
      targetingKey: `${authUser.id}`,
      userId: `${authUser.id}`,
      email: authUser.email,
      userType: authUser.userType,
      ...(authUser.userType === "THERAPIST" && {
        therapistId: `${authUser.userTypeId}`,
      }),
    });
  }, [authUser, isImpersonating]);

  return null;
}

/**
 * Set student info for datadog RUM (for student/guardian exposed pages without an auth user).
 *
 * @param studentInfo
 * @returns
 */
export function SetDatadogRUMStudentInfo(studentInfo: {
  studentId: number;
  studentName: string;
}) {
  if (process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
    datadogRum.setUser(studentInfo);
  }

  return null;
}

function _isReactHooksOrderErrorFromVendorCode(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!isHookCountMismatchError({ message, stack })) {
    return false;
  }
  if (!stack) {
    return true;
  }
  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));
  if (frames.length === 0) {
    return true;
  }
  return frames.every(
    (frame) =>
      frame.includes("react-dom") ||
      frame.includes("react.") ||
      frame.includes("next/dist/") ||
      frame.includes("node_modules") ||
      frame.includes("[native code]") ||
      _isMinifiedFrameworkFrame(frame),
  );
}

function _isMinifiedFrameworkFrame(frame: string): boolean {
  if (!frame.includes("_next/static/chunks/")) {
    return false;
  }
  const hasSourceFilePath =
    frame.includes("/src/") ||
    frame.includes("/app/") ||
    frame.includes("/lib/") ||
    frame.includes("/components/");
  return !hasSourceFilePath;
}

function _isKnownBrowserRuntimeNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  const normalizedMessage = message?.toLowerCase() ?? "";
  const normalizedStack = stack?.toLowerCase() ?? "";

  const isRuntimeTabNotFoundMessage =
    normalizedMessage.includes("runtime.sendmessage()") &&
    normalizedMessage.includes("tab not found");

  if (isRuntimeTabNotFoundMessage) {
    return true;
  }

  if (
    normalizedStack.includes("runtime.sendmessage()") &&
    normalizedStack.includes("tab not found")
  ) {
    return true;
  }

  // Firefox reader-mode scripts injected by iOS browser extensions into
  // non-Firefox browsers (e.g. Mobile Safari). The extension assumes
  // `window.__firefox__` exists, causing a TypeError on Safari/WebKit.
  if (normalizedMessage.includes("window.__firefox__")) {
    return true;
  }

  return false;
}

function _isUnsupportedSyntaxError(
  message: string | undefined,
  stack: string | undefined,
  kind: string | undefined,
): boolean {
  if (!message) {
    return false;
  }
  const UNSUPPORTED_SYNTAX_PATTERNS = [
    "Invalid character: '#'",
    "Unexpected token '#'",
    "Unexpected '#'",
    "Unexpected private name",
    "Cannot parse class method with private name",
  ];
  if (UNSUPPORTED_SYNTAX_PATTERNS.some((p) => message.includes(p))) {
    return true;
  }

  if (kind !== "SyntaxError" && !message.startsWith("SyntaxError")) {
    return false;
  }
  if (!message.includes("Unexpected token")) {
    return false;
  }
  if (!stack) {
    return true;
  }
  const frames = stack.split("\n").filter((l) => l.trim().startsWith("at "));
  return frames.length === 0;
}

function _getErrorKindOrType(
  error: Record<string, unknown>,
): string | undefined {
  if (typeof error.kind === "string") {
    return error.kind;
  }
  if (typeof error.type === "string") {
    return error.type;
  }
  return undefined;
}

function _isTransientResourceFetchUrl(url: string): boolean {
  return url.includes("/api/trpc/") || url.includes("/api/auth/");
}

/**
 * Safari cancels in-flight fetches (e.g. `/api/auth/session`) when the page is
 * being unloaded or when a ChunkLoadError triggers an automatic reload. The
 * resulting `TypeError: cancelled` surfaces as a RUM error with status_code 0
 * and an empty stack trace (`"TypeError: cancelled"`). These are not actionable.
 */
function _isSafariFetchCancelledNoise(error: Record<string, unknown>): boolean {
  const message =
    typeof error.message === "string" ? error.message.toLowerCase() : "";
  const stack = typeof error.stack === "string" ? error.stack : "";

  if (message !== "cancelled") {
    return false;
  }

  return (
    !stack ||
    stack === "TypeError: cancelled" ||
    !stack.split("\n").some((line) => line.trim().startsWith("at "))
  );
}
